import posthog from "posthog-js";
import { captureRecoveryFragment } from "@/lib/abandoned-carts/recovery-fragment";
import { isAutomationUserAgent, readBrowserGeometry, suspectedBotSignals } from "@/lib/posthog-bot-signals";
import { isPostHogSuppressedPath, isRecoveryPath } from "@/lib/posthog-suppressed-path";
import { sanitizePostHogEvent, type PostHogEventLike } from "@/lib/posthog-privacy";
import { createBrowserNoiseEnricher } from "@/lib/posthog-browser-noise";
import { isProSealOnlyExceptionEvent } from "@/lib/provenexpert/pro-seal";

const key = process.env.NEXT_PUBLIC_POSTHOG_KEY ?? process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN;
const apiHost = process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://t.bergdorfbio.com";

const REACT_HYDRATION_MISMATCH = /#418(?!\d)/;

/** Tel links the site renders itself carry `data-bb-tel`; any other one was injected. */
const INJECTED_TEL_LINKS = 'a[href^="tel:"]:not([data-bb-tel])';

function languageBase(value: string | null | undefined): string | null {
	return value?.split("-")[0]?.trim().toLowerCase() || null;
}

/**
 * Counted once here, after the HTML is parsed and before hydration: by the time the
 * `$exception` is sent, React's #418 recovery has re-rendered the failed boundary and
 * dropped the links iOS put into the server DOM.
 */
const injectedTelLinksAtLoad = (() => {
	try {
		return typeof document === "undefined" ? null : document.querySelectorAll(INJECTED_TEL_LINKS).length;
	} catch {
		return null;
	}
})();

/**
 * React #418 (server text/HTML != client) is mostly the browser editing the DOM before
 * hydration: page translation (`translated-ltr/-rtl` on <html>), iOS turning phone-like
 * text into tel links, or a UI language other than the page's. Tag those signals on the
 * `$exception` so browser noise separates from real mismatches. The page language comes
 * from the URL segment, since translators rewrite <html lang>. Never throws.
 */
function withHydrationMismatchContext<T extends PostHogEventLike>(event: T): T {
	try {
		const list = event.properties?.$exception_list;
		if (
			event.event !== "$exception" ||
			typeof document === "undefined" ||
			!Array.isArray(list) ||
			!(list as Array<{ value?: unknown } | null | undefined>).some(
				(entry) => typeof entry?.value === "string" && REACT_HYDRATION_MISMATCH.test(entry.value),
			)
		)
			return event;
		const html = document.documentElement;
		const pageLanguage = languageBase(window.location.pathname.split("/")[1]);
		const browserLanguage = languageBase(typeof navigator === "undefined" ? null : navigator.language);
		return {
			...event,
			properties: {
				...event.properties,
				hydration_browser_translated:
					html.classList.contains("translated-ltr") || html.classList.contains("translated-rtl"),
				hydration_injected_tel_links: document.querySelectorAll(INJECTED_TEL_LINKS).length,
				hydration_injected_tel_links_at_load: injectedTelLinksAtLoad,
				hydration_lang_mismatch: pageLanguage && browserLanguage ? pageLanguage !== browserLanguage : null,
			},
		};
	} catch {
		return event;
	}
}

captureRecoveryFragment();

// Skip initialization itself: loaded/before_send alone run too late for replay.
if (key && (typeof window === "undefined" || !isRecoveryPath(window.location.pathname))) {
	const browserNoise = createBrowserNoiseEnricher();
	// A real interaction ends the scanner suspicion for this document. Synthetic
	// scanner clicks must not remove these listeners before a human arrives.
	try {
		if (typeof document !== "undefined") {
			const interactions = ["pointerdown", "keydown", "touchstart", "click", "wheel"];
			const onInteraction = (event: Event) => {
				if (!event.isTrusted) return;
				browserNoise.noteInteraction();
				for (const name of interactions) document.removeEventListener(name, onInteraction, true);
			};
			for (const name of interactions)
				document.addEventListener(name, onInteraction, { capture: true, passive: true });
		}
	} catch {
		/* Tracking must not block initialization. */
	}
	try {
		posthog.init(key, {
			api_host: apiHost,
			defaults: "2026-01-30",
			capture_exceptions: true,
			// Rageclicks (3+ taps) are captured by default; dead clicks make the
			// milder single-tap unresponsiveness measurable (checkout keyboard bug).
			capture_dead_clicks: true,
			// Attribution only: CLS joins the SDK's INP/LCP default so a shift names its
			// largest target. Whether web vitals run stays with the project config.
			capture_performance: { web_vitals_attribution: ["CLS", "INP", "LCP"] },
			// Replay is unused and was the dominant bill; keep masking ready if we re-enable.
			disable_session_recording: true,
			mask_personal_data_properties: true,
			custom_personal_data_properties: [
				"checkout",
				"transaction",
				"txn",
				"order",
				"token",
				"recovery",
				"recoveryToken",
				"email",
				"query",
			],
			mask_all_element_attributes: true,
			session_recording: {
				maskAllInputs: true,
				maskTextSelector: "[data-posthog-sensitive], [data-posthog-sensitive] *",
				blockSelector: "[data-posthog-block]",
				recordHeaders: false,
				recordBody: false,
			},
			debug: process.env.NODE_ENV === "development",
			before_send: (event) => {
				if (!event) return null;
				// ProSeal is an optional third-party footer widget. Keep all mixed or
				// source-less exceptions; suppress only stacks proven to be entirely theirs.
				if (isProSealOnlyExceptionEvent(event)) return null;
				if (typeof navigator !== "undefined") {
					const ua = navigator.userAgent || "";
					// Drop automation/headless traffic (Playwright/Puppeteer/Selenium/crawlers) so the funnel isn't bot-inflated.
					if ((navigator as { webdriver?: boolean }).webdriver === true || isAutomationUserAgent(ua)) {
						return null;
					}
				}
				if (typeof window !== "undefined" && isPostHogSuppressedPath(window.location.pathname)) {
					return null;
				}
				const enriched = browserNoise.enrich(event, {
					userAgent: typeof navigator === "undefined" ? "" : navigator.userAgent,
					pathname: typeof window === "undefined" ? "" : window.location.pathname,
				});
				return sanitizePostHogEvent(withHydrationMismatchContext(enriched), key.trim());
			},
			loaded: (client) => {
				const release = process.env.NEXT_PUBLIC_POSTHOG_RELEASE?.trim();
				if (release) {
					try {
						client.register({ release });
					} catch {
						// ignore
					}
				}
				// Tag, never drop: reports exclude `suspected_bot`, a misjudged customer stays visible.
				// `loaded` runs before the first $pageview, so the tag is already on it.
				const botSignals = suspectedBotSignals(readBrowserGeometry());
				if (botSignals.length > 0) {
					try {
						client.register({ suspected_bot: true, bot_signals: botSignals.join(",") });
					} catch {
						// ignore
					}
				}
				if (typeof window !== "undefined" && isPostHogSuppressedPath(window.location.pathname)) {
					try {
						client.stopSessionRecording();
					} catch {
						// ignore
					}
				}
			},
		});
	} catch {
		// PostHog init must never crash — it runs before React hydration
	}

	// --- Hydration-mismatch reporter ------------------------------------------
	// React reports hydration mismatches (#418/#423/#425) through Next's internal
	// onRecoverableError -> reportGlobalError; the App Router exposes no hook to
	// override it, and in PRODUCTION React strips the diff + component stack, so we
	// cannot capture the faulting component name client-side. What we CAN do is emit
	// a clean, queryable `hydration_mismatch` event tagged with the pathname/locale
	// (and, in dev/preview where React keeps the detail, the full message) by
	// intercepting console.error — the channel React/Next route these through. This
	// augments the auto-captured $exception with a structured signal to localize the
	// problem area. It must never throw (runs around hydration).
	if (typeof window !== "undefined") {
		const HYDRATION_SIGNATURES = [
			"Hydration failed because",
			"Text content does not match",
			"hydrated but some attributes",
			"Minified React error #418",
			"Minified React error #423",
			"Minified React error #425",
		];
		const reportedHydrationMismatches = new Set<string>();
		const originalConsoleError = console.error.bind(console);
		console.error = (...args: Parameters<typeof console.error>) => {
			try {
				const text = args
					.map((a) => (typeof a === "string" ? a : a instanceof Error ? a.message : ""))
					.join(" ");
				if (text && HYDRATION_SIGNATURES.some((sig) => text.includes(sig))) {
					const reactErrorCode = text.match(/Minified React error #(418|423|425)/)?.[1] ?? "unminified";
					const pathname = window.location.pathname;
					const surface = pathname.includes("/peptide-calculator")
						? "calculator"
						: pathname.includes("/products/")
							? "pdp"
							: pathname.includes("/checkout")
								? "checkout"
								: pathname.includes("/search")
									? "search"
									: "other";
					const dedupeKey = `${reactErrorCode}:${pathname}`;
					if (reportedHydrationMismatches.has(dedupeKey)) {
						originalConsoleError(...args);
						return;
					}
					reportedHydrationMismatches.add(dedupeKey);
					posthog.capture("hydration_mismatch", {
						pathname,
						locale: pathname.split("/")[1] || null,
						react_error_code: reactErrorCode,
						surface,
						...(process.env.NODE_ENV === "development" ? { message_excerpt: text.slice(0, 500) } : {}),
					});
				}
			} catch {
				// reporting must never break the page
			}
			originalConsoleError(...args);
		};
	}
}
